import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import { ArrowLeft, Image, Save, Loader2 } from 'lucide-react';
import { ProductSelector } from '../product-management/ProductSelector';
import { FormSelection, type HubSpotForm } from '../hubspot-management/FormSelection';
import { TargetProfileSelector } from './TargetProfileSelector';
import { buildTargetProfileMap, buildTargetProfileSummary } from '../../utils/targetProfiles';
import RevenueMatrix, { 
  type RevenueMatrixData, 
  createDefaultData, 
  serializeRevenueMatrix, 
  deserializeRevenueMatrix,
  MultiFormRevenueMatrix,
  type CampaignRevenueMatrices,
  serializeCampaignMatrices,
  deserializeCampaignMatrices
} from './RevenueMatrix';
import { type NavigationItem } from '../Navigation';
import { WebsitePathsEditor } from './WebsitePathsEditor';

import { useCampaignApi, useHubSpotFormsApi, useApiService, type UpdateCampaignRequest, type HubSpotFormField, type CampaignReferenceData, type TargetProfileOption } from '../../services/apiService';

interface EditCampaignProps {
  campaignId: string;
  onNavigate: (view: NavigationItem) => void;
}

// Minimal ConceptSummary type used by concepts API call
interface ConceptSummary {
  title: string;
}


interface EditableCampaignState {
  name: string;
  businessUnit: string;
  brandId: number | null;
  brandName: string;
  category: string;
  concept: string;
  status: 'upcoming' | 'active' | 'completed';
  startDate: string;
  endDate: string;
  budget: number;
  campaignType: string;
  platforms: string[];
  products: string[];
  description: string;
  image: string;
  targetMarkets: string[];
  hubSpotFormIds: string[];
  b2bTargetProfileIds: number[];
  b2cTargetProfileIds: number[];
  websitePaths: string[]; // added
}

// Sorted alphabetically (case-sensitive, 'Fresh...' entries first, then 'LU...'):
const businessUnitsList = [
  "Fresh Baltics",
  "Fresh Denmark",
  "Fresh Finland",
  "LU Australia",
  "LU BeNeFrItEs",
  "LU Denmark",
  "LU FIBA",
  "LU Germany",
  "LU Hungary",
  "LU Norway",
  "LU Poland",
  "LU Romania",
  "LU Sweden",
  "LU UK",
  "LU US"
];

const marketOptions = [
  // EU
  "Austria", "Belgium", "Bulgaria", "Croatia", "Cyprus", "Czech Republic", "Denmark", "Estonia", "Finland", "France", "Germany", "Greece", "Hungary", "Ireland", "Italy", "Latvia", "Lithuania", "Luxembourg", "Malta", "Netherlands", "Poland", "Portugal", "Romania", "Slovakia", "Slovenia", "Spain", "Sweden",
  // UK
  "England", "Scotland", "Wales", "Northern Ireland",
  // US, Australia
  "United States", "Australia"
];

function formatDateDDMMYYYY(dateStr: string): string {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  if (!year || !month || !day) return dateStr;
  return `${day}/${month}/${year}`;
}

function formatDateForInput(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toISOString().split('T')[0];
}

function websitePathsValid(paths: string[]): boolean {
  return paths.every(p => p.startsWith('/') && !/\s/.test(p));
}

export default function EditCampaign({ campaignId, onNavigate }: EditCampaignProps) {
  const [campaign, setCampaign] = useState<EditableCampaignState>({
    name: '',
    businessUnit: 'LU Global',
    brandId: null,
    brandName: '',
    category: '',
    concept: 'none',
    status: 'upcoming',
    startDate: '',
    endDate: '',
    budget: 0,
    campaignType: '',
    platforms: [],
    products: [],
    description: '',
    image: '',
    targetMarkets: [],
    hubSpotFormIds: [],
    b2bTargetProfileIds: [],
    b2cTargetProfileIds: [],
    websitePaths: []
  });

  const [referenceData, setReferenceData] = useState<CampaignReferenceData | null>(null);
  const [referenceLoading, setReferenceLoading] = useState(true);
  const [referenceError, setReferenceError] = useState<string | null>(null);

  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Revenue Matrix state
  const [revenueMatrixData, setRevenueMatrixData] = useState<CampaignRevenueMatrices>({ matrices: [] });

  // HubSpot Forms state
  const [hubSpotForms, setHubSpotForms] = useState<HubSpotForm[]>([]);
  const [selectedHubSpotForms, setSelectedHubSpotForms] = useState<Set<string>>(new Set());
  const [hubSpotFormsLoading, setHubSpotFormsLoading] = useState(false);
  const [hubSpotFormsError, setHubSpotFormsError] = useState<string | null>(null);

  // Concepts state
  const api = useApiService();
  const [conceptTitles, setConceptTitles] = useState<string[]>([]);
  const [conceptsLoading, setConceptsLoading] = useState(false);
  const [conceptsError, setConceptsError] = useState<string | null>(null);

  const campaignApi = useCampaignApi();
  const hubSpotFormsApi = useHubSpotFormsApi();

  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        setReferenceLoading(true);
        setReferenceError(null);
        const data = await campaignApi.getReferenceData();
        setReferenceData(data);
      } catch (error) {
        console.error('Error loading campaign reference data:', error);
        setReferenceError('Failed to load target profiles and platforms. Please try again.');
      } finally {
        setReferenceLoading(false);
      }
    };

    loadReferenceData();
  }, []);

  useEffect(() => {
    if (!referenceData || referenceData.brands.length === 0) {
      return;
    }

    setCampaign(prev => {
      if (prev.brandId !== null) {
        return prev;
      }

      const defaultBrand = referenceData.brands[0];
      return {
        ...prev,
        brandId: defaultBrand?.id ?? null,
        brandName: defaultBrand?.name ?? prev.brandName
      };
    });
  }, [referenceData]);

  const targetProfileMap = useMemo(() => {
    if (!referenceData) {
      return new Map<number, TargetProfileOption>();
    }

    return buildTargetProfileMap([
      ...(referenceData.b2bTargetProfiles ?? []),
      ...(referenceData.b2cTargetProfiles ?? [])
    ]);
  }, [referenceData]);

  const targetProfileSummary = useMemo(() => {
    if (targetProfileMap.size === 0) {
      return '';
    }

    return buildTargetProfileSummary(
      campaign.b2bTargetProfileIds,
      campaign.b2cTargetProfileIds,
      targetProfileMap
    );
  }, [campaign.b2bTargetProfileIds, campaign.b2cTargetProfileIds, targetProfileMap]);

  // Load HubSpot forms
  useEffect(() => {
    const loadHubSpotForms = async () => {
      try {
        setHubSpotFormsLoading(true);
        setHubSpotFormsError(null);
        
        const forms = await hubSpotFormsApi.getForms();
        setHubSpotForms(forms);
        
      } catch (error) {
        console.error('Error loading HubSpot forms:', error);
        setHubSpotFormsError('Failed to load HubSpot forms. Please try again.');
      } finally {
        setHubSpotFormsLoading(false);
      }
    };

    loadHubSpotForms();
  }, []);

  // Load concepts
  useEffect(() => {
    const loadConcepts = async () => {
      setConceptsLoading(true);
      setConceptsError(null);
      try {
        const concepts = await api.get<ConceptSummary[]>('/concepts');
        setConceptTitles(concepts.map(c => c.title));
      } catch (err) {
        console.error('Error loading concepts:', err);
        setConceptsError('Failed to load concepts');
      } finally {
        setConceptsLoading(false);
      }
    };

    loadConcepts();
  }, [api]);

  // Ensure current campaign concept stays selectable even if not in fetched list
  const mergedConceptTitles = React.useMemo(() => {
    const set = new Set(conceptTitles);
    const current = campaign.concept;
    if (current && current !== 'none' && !set.has(current)) {
      set.add(current);
    }
    return Array.from(set);
  }, [conceptTitles, campaign.concept]);

  // Load existing campaign data
  useEffect(() => {
    const loadCampaign = async () => {
      try {
        setIsLoading(true);
        setSubmitError(null);
        
        const existingCampaign = await campaignApi.getCampaign(Number(campaignId));
        
        const allowedStatuses: Array<'upcoming' | 'active' | 'completed'> = ['upcoming', 'active', 'completed'];
        const normalizedStatus = allowedStatuses.includes(existingCampaign.status as 'upcoming' | 'active' | 'completed')
          ? (existingCampaign.status as 'upcoming' | 'active' | 'completed')
          : 'upcoming';

        setCampaign({
          name: existingCampaign.name,
          businessUnit: existingCampaign.businessUnit,
          brandId: existingCampaign.brandId ?? null,
          brandName: existingCampaign.brandName || '',
          category: existingCampaign.category,
          concept: existingCampaign.concept || 'none',
          status: normalizedStatus,
          startDate: formatDateForInput(existingCampaign.startDate),
          endDate: formatDateForInput(existingCampaign.endDate),
          budget: existingCampaign.budget,
          campaignType: existingCampaign.campaignType,
          platforms: existingCampaign.platforms || [],
          products: existingCampaign.products || [],
          description: existingCampaign.description || '',
          image: existingCampaign.imageUrl || '',
          targetMarkets: existingCampaign.targetMarkets || [],
          hubSpotFormIds: existingCampaign.hubSpotFormIds || [],
          b2bTargetProfileIds: existingCampaign.b2bTargetProfileIds || [],
          b2cTargetProfileIds: existingCampaign.b2cTargetProfileIds || [],
          websitePaths: existingCampaign.websitePaths || []
        });

        // Set existing image preview if available
        if (existingCampaign.imageUrl) {
          setImagePreview(existingCampaign.imageUrl);
        }

        // Set selected HubSpot forms
        if (existingCampaign.hubSpotFormIds && existingCampaign.hubSpotFormIds.length > 0) {
          setSelectedHubSpotForms(new Set(existingCampaign.hubSpotFormIds));
        }

        // Set revenue matrix data
        if (existingCampaign.revenueMatrix) {
          const matrixData = deserializeCampaignMatrices(existingCampaign.revenueMatrix);
          setRevenueMatrixData(matrixData);
        }

      } catch (error) {
        console.error('Error loading campaign:', error);
        setSubmitError('Failed to load campaign data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadCampaign();
  }, [campaignId]); // Remove campaignApi from dependencies

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setSubmitError('Please select an image file (JPEG, PNG, GIF, or WebP)');
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setSubmitError('Image file size must be less than 10MB');
        return;
      }

      setSelectedImageFile(file);
      
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      
      // Clear any previous error
      setSubmitError(null);
    }
  };

  const handleImageRemove = () => {
    setSelectedImageFile(null);
    setImagePreview(campaign.image); // Reset to original image if it exists
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpdateCampaign = async () => {
    if (!campaign.name || campaign.brandId === null || !campaign.category || !campaign.startDate ||
        !campaign.endDate || !campaign.campaignType) {
      setSubmitError('Please fill in all required fields');
      return;
    }

    if (new Date(campaign.startDate) >= new Date(campaign.endDate)) {
      setSubmitError('End date must be after start date');
      return;
    }

    if (campaign.b2bTargetProfileIds.length === 0 || campaign.b2cTargetProfileIds.length === 0) {
      setSubmitError('Please select at least one B2B and one Consumer profile');
      return;
    }

    if (!websitePathsValid(campaign.websitePaths)) {
      setSubmitError('Each website path must start with "/" and contain no spaces');
      return;
    }

    if (referenceLoading) {
      setSubmitError('Reference data is still loading. Please wait.');
      return;
    }

    if (!referenceData || targetProfileMap.size === 0) {
      setSubmitError('Campaign reference data is unavailable. Please refresh and try again.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      const targetProfileText = targetProfileSummary || 'B2B: None | B2C: None';

      // Create the campaign update request object
      const updateRequest: UpdateCampaignRequest = {
        name: campaign.name,
        businessUnit: campaign.businessUnit,
        brandId: campaign.brandId ?? undefined,
        category: campaign.category,
        concept: campaign.concept === 'none' ? undefined : campaign.concept,
        status: campaign.status,
        startDate: campaign.startDate,
        endDate: campaign.endDate,
        budget: campaign.budget,
        targetProfile: targetProfileText,
        campaignType: campaign.campaignType,
        b2bTargetProfileIds: campaign.b2bTargetProfileIds,
        b2cTargetProfileIds: campaign.b2cTargetProfileIds,
        platforms: campaign.platforms,
        products: campaign.products,
        description: campaign.description || undefined,
        targetMarkets: campaign.targetMarkets,
        hubSpotFormIds: Array.from(selectedHubSpotForms),
        revenueMatrix: serializeCampaignMatrices(revenueMatrixData),
        websitePaths: campaign.websitePaths
      };

      console.log('Updating campaign:', updateRequest);

      // Call the API to update the campaign
      const updatedCampaign = await campaignApi.updateCampaign(Number(campaignId), updateRequest);
      
      console.log('Campaign updated successfully:', updatedCampaign);
      setSubmitSuccess(true);
      
      // Show success message briefly, then navigate back
      setTimeout(() => {
        onNavigate('campaign-dashboard');
      }, 1500);
      
    } catch (error) {
      console.error('Error updating campaign:', error);
      setSubmitError(error instanceof Error ? error.message : 'Failed to update campaign. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePlatformChange = (platform: string, checked: boolean) => {
    setCampaign(prev => ({
      ...prev,
      platforms: checked
        ? Array.from(new Set([...prev.platforms, platform]))
        : prev.platforms.filter(p => p !== platform)
    }));
  };

  const handleProductsChange = (products: string[]) => {
    setCampaign(prev => ({
      ...prev,
      products: products
    }));
  };

  const handleMarketChange = (market: string, checked: boolean) => {
    setCampaign(prev => ({
      ...prev,
      targetMarkets: checked
        ? [...prev.targetMarkets, market]
        : prev.targetMarkets.filter(m => m !== market)
    }));
  };

  // HubSpot form handlers
  const handleHubSpotFormToggle = (formId: string, checked: boolean) => {
    const newSelected = new Set(selectedHubSpotForms);
    if (checked) {
      newSelected.add(formId);
    } else {
      newSelected.delete(formId);
    }
    setSelectedHubSpotForms(newSelected);
    
    // Update campaign state
    setCampaign(prev => ({
      ...prev,
      hubSpotFormIds: Array.from(newSelected)
    }));
  };

  const handleSelectAllHubSpotForms = (checked: boolean) => {
    if (checked) {
      const allFormIds = new Set(hubSpotForms.map(form => form.id));
      setSelectedHubSpotForms(allFormIds);
      setCampaign(prev => ({
        ...prev,
        hubSpotFormIds: Array.from(allFormIds)
      }));
    } else {
      setSelectedHubSpotForms(new Set());
      setCampaign(prev => ({
        ...prev,
        hubSpotFormIds: []
      }));
    }
  };

  const handleRevenueMatrixChange = (data: CampaignRevenueMatrices) => {
    setRevenueMatrixData(data);
  };

  const loadFormFields = async (formId: string): Promise<HubSpotFormField[]> => {
    try {
      return await hubSpotFormsApi.getFormFields(formId);
    } catch (error) {
      console.error(`Error loading form fields for ${formId}:`, error);
      return [];
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#f8f5f0] p-6 flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-600">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Loading campaign data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f5f0] p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onNavigate('campaign-dashboard')}
              className="flex items-center gap-2"
              disabled={isSubmitting}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Campaigns
            </Button>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Edit Campaign</h1>
              <p className="text-gray-600 mt-1">Update the campaign details below</p>
            </div>
          </div>
          <Button 
            onClick={handleUpdateCampaign} 
            className="bg-blue-600 hover:bg-blue-700"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Update Campaign
              </>
            )}
          </Button>
        </div>

        {/* Error/Success Messages */}
        {submitError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error updating campaign</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{submitError}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {submitSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">Campaign updated successfully!</h3>
                <div className="mt-2 text-sm text-green-700">
                  <p>Redirecting to campaign dashboard...</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Form */}
        <Card>
          <CardHeader>
            <CardTitle>Campaign Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Campaign Image */}
            <div className="space-y-2">
              <Label>Campaign Image (1920x1080)</Label>
              {imagePreview ? (
                <div className="space-y-3">
                  <div className="border-2 border-gray-300 rounded-lg p-4">
                    <img 
                      src={imagePreview} 
                      alt="Campaign preview" 
                      className="max-w-full h-48 object-contain mx-auto rounded"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">
                      {selectedImageFile?.name || 'Current image'}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleImageRemove}
                      disabled={isSubmitting}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div 
                    className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-gray-400 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Image className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                    <p className="text-blue-600 cursor-pointer hover:underline text-lg">Upload campaign image</p>
                    <p className="text-sm text-gray-500 mt-2">Recommended: 1920x1080 pixels</p>
                    <p className="text-xs text-gray-500 mt-1">Supported: JPEG, PNG, GIF, WebP (max 10MB)</p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={isSubmitting}
                  />
                </>
              )}
            </div>

            {/* Campaign Name and Business Unit */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name">Campaign Name *</Label>
                <Input
                  id="name"
                  value={campaign.name}
                  onChange={(e) => setCampaign({ ...campaign, name: e.target.value })}
                  placeholder="Enter campaign name"
                  className="h-11"
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="businessUnit">Business Unit *</Label>
                <Select 
                  value={campaign.businessUnit} 
                  onValueChange={(value) => setCampaign({ ...campaign, businessUnit: value })}
                  disabled={isSubmitting}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Select business unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {businessUnitsList.map(unit => (
                      <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Brand and Category */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="brand">Brand *</Label>
                <Select
                  value={campaign.brandId !== null ? campaign.brandId.toString() : ''}
                  onValueChange={(value) => setCampaign(prev => ({ ...prev, brandId: value ? Number(value) : null }))}
                  disabled={isSubmitting || referenceLoading || (referenceData?.brands.length ?? 0) === 0}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder={referenceLoading ? 'Loading brands...' : 'Select brand'} />
                  </SelectTrigger>
                  <SelectContent>
                    {(referenceData?.brands ?? []).map(brand => (
                      <SelectItem key={brand.id} value={brand.id.toString()}>{brand.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!referenceLoading && (referenceData?.brands.length ?? 0) === 0 && (
                  <p className="text-xs text-red-600">No brands available. Please add a brand first.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Category Segment *</Label>
                <Select 
                  value={campaign.category} 
                  onValueChange={(value) => setCampaign({ ...campaign, category: value })}
                  disabled={isSubmitting}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Fast Food">Fast Food</SelectItem>
                    <SelectItem value="Danish Pastry">Danish Pastry</SelectItem>
                    <SelectItem value="French Pastry">French Pastry</SelectItem>
                    <SelectItem value="Wheat Bread">Wheat Bread</SelectItem>
                    <SelectItem value="Rye Bread">Rye Bread</SelectItem>
                    <SelectItem value="Savouries">Savouries</SelectItem>
                    <SelectItem value="Cakes & Desserts">Cakes & Desserts</SelectItem>
                    <SelectItem value="Soft Dough">Soft Dough</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Start Date and End Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="startDate">Start Date *</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={campaign.startDate}
                  onChange={(e) => setCampaign({ ...campaign, startDate: e.target.value })}
                  className="h-11"
                  disabled={isSubmitting}
                />
                {campaign.startDate && (
                  <div className="text-xs text-gray-500">Selected: {formatDateDDMMYYYY(campaign.startDate)}</div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate">End Date *</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={campaign.endDate}
                  onChange={(e) => setCampaign({ ...campaign, endDate: e.target.value })}
                  className="h-11"
                  disabled={isSubmitting}
                />
                {campaign.endDate && (
                  <div className="text-xs text-gray-500">Selected: {formatDateDDMMYYYY(campaign.endDate)}</div>
                )}
              </div>
            </div>

            {/* Campaign Type and Concept */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="campaignType">Campaign Type *</Label>
                <Select 
                  value={campaign.campaignType} 
                  onValueChange={(value) => setCampaign({ ...campaign, campaignType: value })}
                  disabled={isSubmitting}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Select campaign type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Conversions">Conversions</SelectItem>
                    <SelectItem value="Customer Acquisition">Customer Acquisition</SelectItem>
                    <SelectItem value="Event Registration">Event Registration</SelectItem>
                    <SelectItem value="Lead Generation">Lead Generation</SelectItem>
                    <SelectItem value="Lead Nurture">Lead Nurture</SelectItem>
                    <SelectItem value="Online Visibility">Online Visibility</SelectItem>
                    <SelectItem value="Retention/Loyalty">Retention/Loyalty</SelectItem>
                    <SelectItem value="Store Visits">Store Visits</SelectItem>
                    <SelectItem value="Brand Awareness">Brand Awareness</SelectItem>
                    <SelectItem value="Engagement">Engagement</SelectItem>
                    <SelectItem value="Website Traffic">Website Traffic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="concept">Campaign Concept (Optional)</Label>
                <Select 
                  value={campaign.concept} 
                  onValueChange={(value) => setCampaign({ ...campaign, concept: value })}
                  disabled={isSubmitting || conceptsLoading}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder={conceptsLoading ? 'Loading concepts...' : 'Select concept'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No concept</SelectItem>
                    {conceptsError && (
                      <SelectItem value="__error" disabled>Failed to load concepts</SelectItem>
                    )}
                    {mergedConceptTitles.map((title) => (
                      <SelectItem key={title} value={title}>
                        {title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Target Profiles */}
            <div className="space-y-4">
              <Label>Target Profiles *</Label>
              {referenceError && (
                <div className="text-sm text-red-600">{referenceError}</div>
              )}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <TargetProfileSelector
                  title="B2B Target Profiles"
                  options={referenceData?.b2bTargetProfiles ?? []}
                  selectedIds={campaign.b2bTargetProfileIds}
                  onSelectionChange={(ids) => setCampaign(prev => ({ ...prev, b2bTargetProfileIds: ids }))}
                  disabled={isSubmitting || referenceLoading || !!referenceError}
                  loading={referenceLoading}
                />
                <TargetProfileSelector
                  title="Consumer Profiles"
                  options={referenceData?.b2cTargetProfiles ?? []}
                  selectedIds={campaign.b2cTargetProfileIds}
                  onSelectionChange={(ids) => setCampaign(prev => ({ ...prev, b2cTargetProfileIds: ids }))}
                  disabled={isSubmitting || referenceLoading || !!referenceError}
                  loading={referenceLoading}
                />
              </div>
              {campaign.b2bTargetProfileIds.length > 0 && campaign.b2cTargetProfileIds.length > 0 && targetProfileSummary && (
                <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-3">
                  {targetProfileSummary}
                </div>
              )}
            </div>

            {/* Campaign Status */}
            <div className="space-y-2">
              <Label htmlFor="status">Campaign Status</Label>
              <Select
                value={campaign.status}
                onValueChange={(value) => setCampaign(prev => ({ ...prev, status: value as 'upcoming' | 'active' | 'completed' }))}
                disabled={isSubmitting}
              >
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Budget */}
            <div className="space-y-2">
              <Label htmlFor="budget">Budget (SEK) *</Label>
              <Input
                id="budget"
                type="number"
                value={campaign.budget || ''}
                onChange={(e) => setCampaign({ ...campaign, budget: parseInt(e.target.value) || 0 })}
                placeholder="0"
                className="h-11 max-w-md"
                disabled={isSubmitting}
              />
            </div>

            {/* Online Platforms */}
            <div className="space-y-3">
              <Label>Online Platforms</Label>
              <div className="border rounded-md bg-white p-3">
                {referenceLoading && (
                  <div className="text-sm text-gray-500">Loading online platforms...</div>
                )}
                {!referenceLoading && referenceError && (
                  <div className="text-sm text-red-600">Unable to load online platforms.</div>
                )}
                {!referenceLoading && !referenceError && (referenceData?.onlinePlatforms?.length ?? 0) === 0 && (
                  <div className="text-sm text-gray-500">No online platforms available.</div>
                )}
                {!referenceLoading && !referenceError && (referenceData?.onlinePlatforms?.length ?? 0) > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {referenceData?.onlinePlatforms.map(platform => (
                      <div key={platform.id} className="flex items-center space-x-3">
                        <Checkbox
                          id={`platform-${platform.id}`}
                          checked={campaign.platforms.indexOf(platform.name) !== -1}
                          onCheckedChange={(checked) => handlePlatformChange(platform.name, checked === true)}
                          disabled={isSubmitting || referenceLoading || !!referenceError}
                        />
                        <Label htmlFor={`platform-${platform.id}`} className="text-sm font-normal">{platform.name}</Label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Products */}
            <ProductSelector
              selectedProducts={campaign.products}
              onProductsChange={handleProductsChange}
            />

            {/* Target Markets */}
            <div className="space-y-2">
              <Label>Target Markets</Label>
              <div className="border rounded p-3 bg-white">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                  {marketOptions.map(market => (
                    <label key={market} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={campaign.targetMarkets.indexOf(market) !== -1}
                        onChange={e => handleMarketChange(market, e.target.checked)}
                        disabled={isSubmitting}
                      />
                      {market}
                    </label>
                  ))}
                </div>
                {campaign.targetMarkets.length > 0 && (
                  <div className="mt-2 text-xs text-gray-600">
                    Selected: {campaign.targetMarkets.join(", ")}
                  </div>
                )}
              </div>
            </div>

            {/* HubSpot Forms Selection */}
            <div className="space-y-3">
              <Label>HubSpot Forms (Optional)</Label>
              <div className="text-sm text-gray-600 mb-3">
                Select HubSpot forms that will be associated with this campaign for lead capture and tracking.
              </div>
              
              {hubSpotFormsError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700">{hubSpotFormsError}</p>
                </div>
              )}

              <Card>
                <CardContent className="p-4">
                  <FormSelection
                    forms={hubSpotForms}
                    selectedForms={selectedHubSpotForms}
                    onFormToggle={handleHubSpotFormToggle}
                    onSelectAll={handleSelectAllHubSpotForms}
                    loading={hubSpotFormsLoading}
                  />
                </CardContent>
              </Card>

              {selectedHubSpotForms.size > 0 && (
                <div className="text-sm text-gray-600">
                  <strong>{selectedHubSpotForms.size}</strong> form{selectedHubSpotForms.size !== 1 ? 's' : ''} selected
                </div>
              )}
            </div>

            {/* Description */}
            {/* Website Paths Editor */}
            <div className="space-y-2">
              <Label>Website Paths (Optional)</Label>
              <WebsitePathsEditor
                paths={campaign.websitePaths}
                onChange={(paths) => setCampaign(prev => ({ ...prev, websitePaths: paths }))
                }
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                value={campaign.description}
                onChange={(e) => setCampaign({ ...campaign, description: e.target.value })}
                placeholder="Add campaign description, goals, or notes..."
                className="min-h-[120px] resize-none"
                disabled={isSubmitting}
              />
            </div>

            {/* Revenue Matrix */}
            <div className="space-y-3">
              <MultiFormRevenueMatrix
                selectedFormIds={selectedHubSpotForms}
                formsData={hubSpotForms}
                campaignMatrices={revenueMatrixData}
                onChange={handleRevenueMatrixChange}
                onLoadFormFields={loadFormFields}
                disabled={isSubmitting}
              />
            </div>
          </CardContent>
        </Card>

        {/* Footer Actions */}
        <div className="flex justify-end gap-4 pb-8">
          <Button 
            variant="outline" 
            onClick={() => onNavigate('campaign-dashboard')}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateCampaign} 
            className="bg-blue-600 hover:bg-blue-700"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Update Campaign
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}